import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ApiError,
  exportBaselineCsv,
  fetchBaselineView,
  fetchFinanceSnapshots,
  restoreBaseline,
  softDeleteBaseline,
} from '@/api/client'
import type { ForecastSnapshot, PublishedSnapshotLine } from '@/api/types'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { ControlRow } from '@/components/ControlRow'
import { EmptyState } from '@/components/EmptyState'
import { ErrorState } from '@/components/ErrorState'
import { HubPage } from '@/components/HubPage'
import { LoadingState } from '@/components/LoadingState'
import { Money } from '@/components/Money'
import { PageHelp } from '@/components/PageHelp'
import { useAuth } from '@/auth/useAuth'
import { isViewerUser, isFinanceUser } from '@/api/types'
import { useAppChrome } from '@/context/AppChromeContext'
import { formatDateTime } from '@/lib/format-date'
import { formatMoney, formatMoneyCompact } from '@/lib/format-money'
import { probabilityTone } from '@/lib/format-probability'
import { useToast } from '@/components/toast-context'

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

function amountFromUnknown(raw: string | number | null | undefined): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'string') {
    const cleaned = raw.replace(/[^0-9.-]/g, '')
    const numeric = Number(cleaned)
    if (Number.isFinite(numeric)) return numeric
  }
  return null
}

/** Read a headline total that may be a number or a legacy display string. */
function headlineAmount(
  totals: Record<string, unknown>,
  primary: string,
  legacy?: string,
): number | null {
  const raw = totals[primary] ?? (legacy ? totals[legacy] : undefined)
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'string') {
    const cleaned = raw.replace(/[^0-9.-]/g, '')
    const numeric = Number(cleaned)
    if (Number.isFinite(numeric)) return numeric
  }
  return null
}

function formatProbability(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${Math.round(value * 100)}%`
}

function monthAmounts(line: PublishedSnapshotLine): number[] {
  const map = new Map<number, number>()
  for (const row of line.months ?? []) {
    const amount = amountFromUnknown(row.display || row.amount)
    if (amount != null) map.set(row.month, amount)
  }
  return MONTH_LABELS.map((_, index) => map.get(index + 1) ?? 0)
}

function lineMeta(line: PublishedSnapshotLine, currency: string): string {
  const parts: string[] = []
  if (line.stage_name?.trim()) parts.push(line.stage_name.trim())
  parts.push(formatProbability(line.stage_probability))
  const billRate = amountFromUnknown(line.bill_rate)
  if (billRate != null) parts.push(`${formatMoney(billRate, currency)}/hr`)
  if (line.base_headcount != null) parts.push(`${line.base_headcount} HC`)
  return parts.join(' · ')
}

function MonthCell({ amount, currency }: { amount: number; currency: string }) {
  const blank = !(amount > 0)
  return (
    <td
      className={`table__num published-grid__month${blank ? ' published-grid__month--blank' : ''}`}
      title={blank ? undefined : formatMoney(amount, currency)}
    >
      {blank ? null : formatMoneyCompact(amount, currency)}
    </td>
  )
}

export function PublishedPage() {
  const { user } = useAuth()
  const { year } = useAppChrome()
  const toast = useToast()
  const queryClient = useQueryClient()
  const finance = isFinanceUser(user)
  const reviewerOnly = isViewerUser(user) && !finance

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [restoreTarget, setRestoreTarget] = useState<ForecastSnapshot | null>(null)
  const [restoreReason, setRestoreReason] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<ForecastSnapshot | null>(null)

  const snapshotsQuery = useQuery({
    queryKey: ['finance', 'snapshots', year],
    queryFn: () => fetchFinanceSnapshots(year),
  })

  const snapshots = snapshotsQuery.data ?? []
  const selected = snapshots.find((row) => row.id === selectedId) ?? null

  useEffect(() => {
    const rows = snapshotsQuery.data ?? []
    if (!rows.length) {
      setSelectedId(null)
      return
    }
    // list_snapshots is published_at desc — first row is the latest baseline
    const first = rows[0]
    if (first && (!selectedId || !rows.some((row) => row.id === selectedId))) {
      setSelectedId(first.id)
    }
  }, [snapshotsQuery.data, selectedId])

  useEffect(() => {
    setFilter('')
  }, [selectedId])

  const detailQuery = useQuery({
    queryKey: ['finance', 'baseline', selectedId],
    queryFn: () => fetchBaselineView(selectedId!),
    enabled: Boolean(selectedId),
  })

  const published = detailQuery.data ?? null
  const totals = published?.headline_totals as Record<string, unknown> | null | undefined
  const currency = String(totals?.currency ?? 'USD')

  const lines = useMemo(() => {
    const rows = [...(published?.lines ?? [])]
    rows.sort((a, b) => {
      const customer = (a.customer_name ?? '').localeCompare(b.customer_name ?? '', undefined, {
        sensitivity: 'base',
      })
      if (customer !== 0) return customer
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    })
    const needle = filter.trim().toLowerCase()
    if (!needle) return rows
    return rows.filter((line) => {
      const hay = `${line.customer_name ?? ''} ${line.name} ${line.stage_name ?? ''}`.toLowerCase()
      return hay.includes(needle)
    })
  }, [published?.lines, filter])

  const portfolioMonths = useMemo(() => {
    const sums = Array.from({ length: 12 }, () => 0)
    for (const line of lines) {
      monthAmounts(line).forEach((amount, index) => {
        sums[index] = (sums[index] ?? 0) + amount
      })
    }
    return sums
  }, [lines])

  const portfolioYear = useMemo(
    () => portfolioMonths.reduce((sum, value) => sum + value, 0),
    [portfolioMonths],
  )

  const restoreMutation = useMutation({
    mutationFn: () => restoreBaseline(restoreTarget!.id, { reason: restoreReason.trim() }),
    onSuccess: (result) => {
      toast.success(
        `Restored “${result.name}” (${result.restored_count} opportunities). Live forecast updated — reload open editors.`,
      )
      setRestoreTarget(null)
      setRestoreReason('')
      void queryClient.invalidateQueries({ queryKey: ['opportunities'] })
      void queryClient.invalidateQueries({ queryKey: ['opportunity'] })
      void queryClient.invalidateQueries({ queryKey: ['finance'] })
      void queryClient.invalidateQueries({ queryKey: ['forecast'] })
    },
    onError: (caught) => {
      toast.error(caught instanceof ApiError ? caught.message : 'Restore failed.')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => softDeleteBaseline(deleteTarget!.id),
    onSuccess: (row) => {
      toast.success(`Baseline “${row.name}” removed.`)
      setDeleteTarget(null)
      if (selectedId === row.id) setSelectedId(null)
      void queryClient.invalidateQueries({ queryKey: ['finance', 'snapshots'] })
      void queryClient.invalidateQueries({ queryKey: ['finance', 'baseline'] })
    },
    onError: (caught) => {
      toast.error(caught instanceof ApiError ? caught.message : 'Could not remove baseline.')
    },
  })

  const exportMutation = useMutation({
    mutationFn: (row: ForecastSnapshot) =>
      exportBaselineCsv(row.id, `aries-baseline-${row.forecast_year}-${row.name}.csv`),
    onSuccess: () => toast.success('Baseline export started — check your downloads.'),
    onError: (caught) => {
      toast.error(caught instanceof ApiError ? caught.message : 'Export failed.')
    },
  })

  const weightedTotal = totals
    ? headlineAmount(totals, 'weighted_total', 'total_weighted_display')
    : null
  const committedTotal = totals ? headlineAmount(totals, 'committed_total') : null
  const upsideTotal = totals ? headlineAmount(totals, 'upside_total') : null
  const commentary = published?.commentary?.trim() ?? ''

  return (
    <HubPage
      className="published-page"
      chrome={
        <>
          <header className="page__header published-page__header">
            <div className="page__header-lead">
              <div>
                <h1 className="page__title">Published baselines</h1>
                <p className="page__subtitle">
                  {reviewerOnly
                    ? `Read-only ${year} baselines published by Finance.`
                    : `Named ${year} forecast baselines — export, restore, or remove.`}
                </p>
              </div>
              {snapshots.length === 0 ? <PageHelp /> : null}
            </div>
          </header>

          {snapshotsQuery.isError && (
            <ErrorState
              message="Could not load baselines."
              onRetry={() => void snapshotsQuery.refetch()}
            />
          )}

          {!snapshotsQuery.isLoading && snapshots.length === 0 && !snapshotsQuery.isError && (
            <EmptyState title={`No baselines published for ${year} yet.`} />
          )}

          {snapshots.length > 0 && (
            <ControlRow
              sticky={false}
              leading={
                <label className="published-page__baseline-field">
                  <span className="visually-hidden">Baseline</span>
                  <select
                    className="field__input published-page__baseline-select"
                    value={selectedId ?? ''}
                    onChange={(event) => setSelectedId(event.target.value || null)}
                    aria-label="Select baseline"
                  >
                    {snapshots.map((row, index) => (
                      <option key={row.id} value={row.id}>
                        {index === 0 ? `Latest · ${row.name}` : row.name}
                        {` · ${formatDateTime(row.published_at)}`}
                      </option>
                    ))}
                  </select>
                </label>
              }
              actions={
                finance && selected ? (
                  <>
                    <button
                      type="button"
                      className="button button--ghost"
                      disabled={exportMutation.isPending}
                      onClick={() => exportMutation.mutate(selected)}
                    >
                      Export CSV
                    </button>
                    <button
                      type="button"
                      className="button button--ghost"
                      onClick={() => setRestoreTarget(selected)}
                    >
                      Restore
                    </button>
                    <button
                      type="button"
                      className="button button--danger"
                      onClick={() => setDeleteTarget(selected)}
                    >
                      Remove
                    </button>
                  </>
                ) : null
              }
            >
              <input
                type="search"
                className="field__input published-page__filter"
                placeholder="Filter customer or opportunity…"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                aria-label="Filter published lines"
                disabled={!published}
              />
            </ControlRow>
          )}

          {published && (
            <div className="published-page__meta">
              <dl className="summary-strip published-page__totals">
                {weightedTotal != null && (
                  <div className="summary-strip__item">
                    <dt className="summary-strip__label">Weighted</dt>
                    <dd>
                      <Money amount={weightedTotal} currency={currency} />
                    </dd>
                  </div>
                )}
                {committedTotal != null && (
                  <div className="summary-strip__item">
                    <dt className="summary-strip__label">Committed</dt>
                    <dd>
                      <Money amount={committedTotal} currency={currency} />
                    </dd>
                  </div>
                )}
                {upsideTotal != null && (
                  <div className="summary-strip__item">
                    <dt className="summary-strip__label">Upside</dt>
                    <dd>
                      <Money amount={upsideTotal} currency={currency} />
                    </dd>
                  </div>
                )}
                <div className="summary-strip__item">
                  <dt className="summary-strip__label">Lines</dt>
                  <dd>
                    {lines.length}
                    {filter.trim() && published.lines.length !== lines.length
                      ? ` / ${published.lines.length}`
                      : ''}
                  </dd>
                </div>
              </dl>
              {commentary ? (
                <details className="published-page__commentary">
                  <summary>Commentary</summary>
                  <p>{commentary}</p>
                </details>
              ) : null}
            </div>
          )}
        </>
      }
    >
      {snapshotsQuery.isLoading && (
        <LoadingState variant="skeleton-block" label="Loading baselines…" />
      )}

      {snapshots.length > 0 && detailQuery.isLoading && (
        <LoadingState variant="skeleton-block" label="Loading baseline…" />
      )}

      {detailQuery.isError && (
        <ErrorState
          message="Could not load this baseline."
          onRetry={() => void detailQuery.refetch()}
        />
      )}

      {published && lines.length === 0 && (
        <EmptyState
          title={filter.trim() ? 'No lines match this filter.' : 'This baseline has no lines.'}
        />
      )}

      {published && lines.length > 0 && (
        <div className="table-pane table-pane--fill published-page__table-pane">
          <div className="table-pane__scroll">
            <table className="table table--dense published-grid">
              <caption className="visually-hidden">
                Published baseline {published.name} for {published.forecast_year}: opportunities
                with monthly amounts
              </caption>
              <colgroup>
                <col className="published-grid__col-customer" />
                <col className="published-grid__col-opportunity" />
                {MONTH_LABELS.map((label) => (
                  <col key={label} className="published-grid__col-month" />
                ))}
                <col className="published-grid__col-year" />
                <col className="published-grid__col-year" />
              </colgroup>
              <thead className="table__head--sticky">
                <tr>
                  <th scope="col" className="table__sticky-col published-grid__customer">
                    Customer
                  </th>
                  <th scope="col" className="table__sticky-col-2 published-grid__opportunity">
                    Opportunity
                  </th>
                  {MONTH_LABELS.map((label) => (
                    <th key={label} scope="col" className="table__num published-grid__month">
                      {label}
                    </th>
                  ))}
                  <th scope="col" className="table__num published-grid__year">
                    Year
                  </th>
                  <th scope="col" className="table__num published-grid__year">
                    Wtd
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="published-grid__portfolio">
                  <th scope="row" className="table__sticky-col published-grid__customer">
                    All lines
                  </th>
                  <td className="table__sticky-col-2 published-grid__opportunity">
                    <span className="published-grid__meta">
                      {lines.length} opportunit{lines.length === 1 ? 'y' : 'ies'}
                    </span>
                  </td>
                  {portfolioMonths.map((amount, index) => (
                    <MonthCell key={MONTH_LABELS[index]} amount={amount} currency={currency} />
                  ))}
                  <td className="table__num published-grid__year">
                    {portfolioYear > 0 ? formatMoneyCompact(portfolioYear, currency) : null}
                  </td>
                  <td className="table__num published-grid__year" />
                </tr>
                {lines.map((line) => {
                  const amounts = monthAmounts(line)
                  const yearTotal =
                    amountFromUnknown(line.year_total_unweighted) ??
                    amountFromUnknown(line.year_total_display) ??
                    amounts.reduce((sum, value) => sum + value, 0)
                  const weighted = amountFromUnknown(line.year_total_weighted)
                  const meta = lineMeta(line, currency)
                  const tone = probabilityTone(line.stage_probability)
                  return (
                    <tr
                      key={line.opportunity_id}
                      className={`published-grid__row published-grid__row--${tone}`}
                    >
                      <th
                        scope="row"
                        className="table__sticky-col published-grid__customer"
                        title={line.customer_name?.trim() || undefined}
                      >
                        {line.customer_name?.trim() || '—'}
                      </th>
                      <td className="table__sticky-col-2 published-grid__opportunity" title={meta}>
                        <span className="published-grid__name">{line.name}</span>
                        <span className="published-grid__meta">{meta}</span>
                      </td>
                      {amounts.map((amount, index) => (
                        <MonthCell key={MONTH_LABELS[index]} amount={amount} currency={currency} />
                      ))}
                      <td
                        className="table__num published-grid__year"
                        title={yearTotal > 0 ? formatMoney(yearTotal, currency) : undefined}
                      >
                        {yearTotal > 0 ? formatMoneyCompact(yearTotal, currency) : null}
                      </td>
                      <td
                        className="table__num published-grid__year"
                        title={
                          weighted != null && weighted > 0
                            ? formatMoney(weighted, currency)
                            : undefined
                        }
                      >
                        {weighted != null && weighted > 0
                          ? formatMoneyCompact(weighted, currency)
                          : null}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(restoreTarget)}
        title={`Restore baseline “${restoreTarget?.name ?? ''}”?`}
        confirmLabel="Restore baseline"
        danger
        busy={restoreMutation.isPending}
        onCancel={() => {
          setRestoreTarget(null)
          setRestoreReason('')
        }}
        onConfirm={() => {
          if (!restoreReason.trim()) {
            toast.error('Enter a reason before restoring.')
            return
          }
          restoreMutation.mutate()
        }}
      >
        <p>
          This overwrites the live {restoreTarget?.forecast_year} forecast for opportunities in this
          baseline. Closed months and uploaded actuals are never changed. Opportunities created
          after this baseline are left alone. Anyone mid-edit should reload.
        </p>
        <label className="field">
          <span className="field__label">Reason *</span>
          <textarea
            className="field__input field__input--textarea"
            rows={3}
            value={restoreReason}
            onChange={(event) => setRestoreReason(event.target.value)}
            placeholder="Why are you restoring this baseline?"
            required
          />
        </label>
      </ConfirmDialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={`Remove baseline “${deleteTarget?.name ?? ''}”?`}
        confirmLabel="Remove"
        danger
        busy={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate()}
      >
        <p>
          The baseline is hidden from the list but kept for audit. It cannot be used for restore
          after removal.
        </p>
      </ConfirmDialog>
    </HubPage>
  )
}
